/**
 * editorStore — AK-321 «Редактор контура — обвести поле» v1.1 + AK-371 v2.
 *
 * ОТДЕЛЬНЫЙ лёгкий zustand-стор (как measureStore из AK-320), НЕ часть
 * mapReducer/useMapStoreV2. ADR-038: редактор (mutable, terra-draw) изолирован
 * от measure-контроллера (read-only) и от карты, чтобы:
 *   - не раздувать reducer и не задевать collision/pills-инварианты (#359),
 *   - держать две edit-модели раздельно (measure = своя машина состояний,
 *     editor = terra-draw select-mode в namespace `td-edit-`).
 *
 * Стор хранит только UI-уровень: активен ли редактор, последний посчитанный
 * результат (площадь/периметр в га) для панели, и флаг «прилипания» для
 * визуального фидбэка. Геометрия живёт в terra-draw store (внутри
 * ContourEditorController), сюда стекает только готовый результат.
 *
 * AK-371 v2 (модель «фигуры-первыми», founder-UAT #443): редактор держит НАБОР
 * независимых фигур. Панель шлёт «нарисуй фигуру» (drawShape) — фигура ДОБАВЛЯЕТСЯ,
 * не заменяет. Когда фигуры пересекаются (shapesIntersect в контроллере →
 * canBoolean), панель открывает кнопки «Вырезать»/«Объединить». «Вырезать» →
 * режим выбора цели кликом (cutMode) с подсказкой.
 */
import { create } from 'zustand';
import type { CircleMode } from '../measure/measureStore';

/** AK-371 v2 — примитив, который рисуем следующим drag'ом/кликами. */
export type ShapeTool = 'rectangle' | 'circle' | 'polygon';

/** Результат редактирования для панели (готовые форматированные строки + сырьё). */
export interface EditorResult {
  /** Основная строка (площадь — га крупно). */
  primary: string;
  /** Вторичная строка (периметр + контекст-единица), опц. */
  secondary?: string;
  /** Число вершин внешнего кольца (для подсказки про удаление). */
  vertexCount: number;
  /** Сырьё для возможного экспорта/отладки. */
  raw: {
    areaSqm: number;
    perimeterMeters: number;
  };
}

interface EditorStore {
  /** Редактор активен (terra-draw start, слушатели навешены). */
  active: boolean;
  /** Идёт ли загрузка геометрии поля при «обвести поле». */
  loading: boolean;
  /** Текст ошибки (геометрия не найдена / сеть), либо null. */
  error: string | null;
  /** Есть ли сейчас хотя бы одна фигура на карте. */
  hasFeature: boolean;
  /** Последний результат (null пока ничего не нарисовано). */
  result: EditorResult | null;
  /** Сейчас вершина прилипает к границе поля (для визуального фидбэка). */
  snapping: boolean;
  /** Доступен ли откат (есть предыдущий снимок в истории). */
  canUndo: boolean;
  /** Доступен ли повтор (redo-стек не пуст). */
  canRedo: boolean;
  /** Сколько фигур сейчас в наборе (для подсказок панели «N фигур»). */
  shapeCount: number;
  /**
   * AK-371 v2 — фигуры пересекаются → доступны булевы «Вырезать»/«Объединить».
   * «Объединить» доступна и без пересечения (склейка в мультиполигон), но
   * «Вырезать» имеет смысл только при пересечении/вложенности.
   */
  canCut: boolean;
  /** Активен режим «Вырезать»: ждём клик по фигуре-цели (подсветка подсказки). */
  cutMode: boolean;
  /** Активный инструмент-фигура (для подсветки кнопки палитры), либо null. */
  activeTool: ShapeTool | null;
  /** Режим рисования круга: от центра / от края до края (диаметр). */
  circleMode: CircleMode;
  /**
   * Монотонный счётчик «очистить всё». Панель инкрементит его,
   * контроллер (через подписку в v2.tsx) дёргает ctrl.clear() на изменение.
   * Сигнал, а не вызов метода — стор не знает про imperative-контроллер.
   */
  clearSignal: number;
  /** Монотонный счётчик «отменить» (undo). Контроллер дёргает ctrl.undo(). */
  undoSignal: number;
  /** Монотонный счётчик «вернуть» (redo). Контроллер дёргает ctrl.redo(). */
  redoSignal: number;
  /**
   * AK-371 v2 — монотонный счётчик «начать рисовать фигуру». Панель кладёт
   * drawTool + инкрементит; контроллер дёргает ctrl.beginShape(tool). Фигура
   * ДОБАВЛЯЕТСЯ к набору (не заменяет). Сигнал, а не прямой вызов — тот же
   * паттерн, что clear/undo/redo.
   */
  drawSignal: number;
  /** Последний запрошенный инструмент рисования. */
  drawTool: ShapeTool | null;
  /** Монотонный счётчик «Объединить» (union всех фигур). */
  unionSignal: number;
  /**
   * Монотонный счётчик «Вырезать» — переключает cutMode в контроллере (вход в
   * режим выбора цели кликом). Повторный клик по кнопке отменяет режим.
   */
  cutSignal: number;
  /** «Готово» на незамкнутом контуре — просим контроллер замкнуть его сам. */
  finishDrawSignal: number;

  /** Включить редактор (контроллер навешивает terra-draw по подписке). */
  activate: () => void;
  /** Полностью выключить редактор (контроллер чистит terra-draw по подписке). */
  deactivate: () => void;
  /** Замкнуть рисуемый полигон (инкремент finishDrawSignal). */
  finishDrawing: () => void;
  /** Очистить все фигуры (инкремент clearSignal). */
  clearFeature: () => void;
  /** Отменить последнюю правку (инкремент undoSignal). */
  undo: () => void;
  /** Повторить отменённую правку (инкремент redoSignal). */
  redo: () => void;
  /** AK-371 v2 — начать рисовать фигуру заданным инструментом (добавит к набору). */
  drawShape: (tool: ShapeTool) => void;
  /** AK-371 v2 — объединить все фигуры в одно поле (union). */
  unionShapes: () => void;
  /** AK-371 v2 — войти/выйти из режима «Вырезать» (выбор цели кликом). */
  toggleCut: () => void;
  /** Переключить режим рисования круга (центр ↔ диаметр). */
  setCircleMode: (mode: CircleMode) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setHasFeature: (hasFeature: boolean) => void;
  setResult: (result: EditorResult | null) => void;
  setSnapping: (snapping: boolean) => void;
  /** Обновить доступность undo/redo (зовёт контроллер через onHistoryChange). */
  setHistory: (state: { canUndo: boolean; canRedo: boolean }) => void;
  /**
   * AK-371 v2 — контроллер сообщает состояние набора фигур: сколько их, можно ли
   * резать (пересечение), активен ли режим выреза, активный инструмент.
   */
  setShapeState: (state: {
    shapeCount: number;
    canCut: boolean;
    cutMode: boolean;
    activeTool: ShapeTool | null;
  }) => void;
}

export const useEditorStore = create<EditorStore>((set) => ({
  active: false,
  loading: false,
  error: null,
  hasFeature: false,
  result: null,
  snapping: false,
  canUndo: false,
  canRedo: false,
  shapeCount: 0,
  canCut: false,
  cutMode: false,
  activeTool: null,
  circleMode: 'center',
  clearSignal: 0,
  undoSignal: 0,
  redoSignal: 0,
  drawSignal: 0,
  drawTool: null,
  unionSignal: 0,
  cutSignal: 0,
  finishDrawSignal: 0,

  activate: () =>
    set({
      active: true,
      error: null,
      result: null,
      canUndo: false,
      canRedo: false,
      shapeCount: 0,
      canCut: false,
      cutMode: false,
      activeTool: null,
    }),
  deactivate: () =>
    set({
      active: false,
      loading: false,
      error: null,
      hasFeature: false,
      result: null,
      snapping: false,
      canUndo: false,
      canRedo: false,
      shapeCount: 0,
      canCut: false,
      cutMode: false,
      activeTool: null,
    }),
  clearFeature: () =>
    set((s) => ({
      clearSignal: s.clearSignal + 1,
      result: null,
      hasFeature: false,
      canUndo: false,
      canRedo: false,
      shapeCount: 0,
      canCut: false,
      cutMode: false,
      activeTool: null,
    })),
  undo: () => set((s) => ({ undoSignal: s.undoSignal + 1 })),
  redo: () => set((s) => ({ redoSignal: s.redoSignal + 1 })),
  drawShape: (tool) =>
    set((s) => ({ drawSignal: s.drawSignal + 1, drawTool: tool, activeTool: tool, cutMode: false })),
  unionShapes: () => set((s) => ({ unionSignal: s.unionSignal + 1, cutMode: false })),
  toggleCut: () => set((s) => ({ cutSignal: s.cutSignal + 1, activeTool: null })),
  finishDrawing: () => set((s) => ({ finishDrawSignal: s.finishDrawSignal + 1 })),
  setCircleMode: (circleMode) => set({ circleMode }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setHasFeature: (hasFeature) => set({ hasFeature }),
  setResult: (result) => set({ result }),
  setSnapping: (snapping) => set({ snapping }),
  setHistory: ({ canUndo, canRedo }) => set({ canUndo, canRedo }),
  setShapeState: ({ shapeCount, canCut, cutMode, activeTool }) =>
    set({ shapeCount, canCut, cutMode, activeTool }),
}));
